import { FC } from 'react';
import { Typography, Box, Button } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';

import { Campaign } from '@libs/models';
import { CampaignUpdateDto } from '@libs/dto';
import { CampaignStatus } from '@libs/types';
import { useCampaign } from '@carcareers/hooks';
import { useSnackbar } from '@carcareers/contexts';

interface CampaignActionsProps {
  campaign?: Campaign;
  onSuccess?: () => void;
}

interface CampaignActions {
  none?: boolean;
  canActivate?: boolean;
  canPause?: boolean;
  canComplete?: boolean;
  canCancel?: boolean;
}

export const CampaignActions: FC<CampaignActionsProps> = ({ campaign, onSuccess }) => {
  const { updateCampaign } = useCampaign();
  const { toggleSnackbar } = useSnackbar();

  const handleSubmit = async (newStatus: CampaignStatus) => {
    const updateDto: CampaignUpdateDto = {
      id: campaign?.id,
      status: newStatus,
    };

    const response = await updateCampaign(updateDto, { onSuccess });

    if (response?.data) {
      toggleSnackbar('success', 'Campaign status updated');
    } else {
      toggleSnackbar('error', 'Failed to update campaign status');
    }
  };

  return (
    <>
      <Typography variant="h5" sx={{ my: 2 }}>
        Actions
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<PlayArrowIcon />}
          onClick={() => handleSubmit(CampaignStatus.ACTIVE)}
          size="large"
        >
          Activate
        </Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={<PauseIcon />}
          onClick={() => handleSubmit(CampaignStatus.PAUSED)}
          size="large"
        >
          Pause
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<DoneIcon />}
          onClick={() => handleSubmit(CampaignStatus.COMPLETED)}
          size="large"
        >
          Complete
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<CloseIcon />}
          onClick={() => handleSubmit(CampaignStatus.CANCELLED)}
          size="large"
        >
          Cancel
        </Button>
      </Box>
    </>
  );
};
